import { Add, Check, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  deDE,
} from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ILegalClientFullObject } from '../Interfaces/ILegalClient';
import { State } from '../redux/mainReducer';
import { AddFromArnotopDialog } from './Arnotop/AddFromArnotopDialog';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { ClientDasboard } from './ClientDashboard/ClientDasboard';
import {
  useGetDocumentRequested,
  useGetDocumentUploaded,
  useGetMetadataLogin,
} from '../hooks/data_login';
import { ClientDasboardButton } from './ClientDashboard/ClientDasboardButton';
import {
  useGetLegalClientLogs,
  useGetLegalClients,
} from '../hooks/reactQuerys/LegalClientQueries';
import { useGetDownloadedDocuments } from '../hooks/reactQuerys/DocumentQueries';

export const ClientsOverview: React.FC = () => {
  const navigate = useNavigate();

  const { legalClients, isLoadingLegalClients, refetchLegalClients } =
    useGetLegalClients();

  const {
    metadataLoginArray,
    isLoadingMetadataLoginArray,
    refetchMetadataLoginArray,
  } = useGetMetadataLogin();

  const {
    downloadedDocuments,
    isLoadingDownloadedDocuments,
    refetchDownloadedDocuments,
  } = useGetDownloadedDocuments();
  const {
    documentRequestedArray,
    isLoadingDocumentRequestedArray,
    refetchDocumentRequestedArray,
  } = useGetDocumentRequested();

  const {
    documentUploadedArray,
    isLoadingDocumentUploadedArray,
    refetchDocumentUploadedArray,
  } = useGetDocumentUploaded();

  const { legalClientLogs, isLoadingLegalClientLogs, refetchLegalClientLogs } =
    useGetLegalClientLogs();

  const groups = useSelector(
    (state: State) => state.contactGroup.storeContactGroup
  );

  const [search, setSearch] = useState('');

  const [showArnoTopNoSyncDialog, setShowArnoTopNoSyncDialog] = useState(false);

  const [activeDashboard, setActiveDashboard] = useState(() => {
    const settingDashboard = JSON.parse(
      String(localStorage.getItem('client_dashboard'))
    );
    if (settingDashboard === null) {
      const returnArray = [
        'PieChartLoggedUser',
        'PieChartUser',
        'ComposedChartLogin',
        'ComposedDocuments',
      ];
      localStorage.setItem('client_dashboard', JSON.stringify(returnArray));
      return returnArray;
    } else {
      return settingDashboard;
    }
  });
  const setActiveDashboardWrapper = (setting: string[]) => {
    setActiveDashboard(setting);
    localStorage.setItem('client_dashboard', JSON.stringify(setting));
  };

  const filteredLegalClients = useMemo(() => {
    if (!legalClients) return [];

    if (!search) return legalClients;

    return legalClients.filter(
      ({ SurName, FirstName, LastName, Email }) =>
        (LastName != undefined &&
          LastName.toLocaleLowerCase().includes(search.toLocaleLowerCase())) ||
        (SurName != undefined &&
          SurName.toLocaleLowerCase().includes(search.toLocaleLowerCase())) ||
        (FirstName != undefined &&
          FirstName.toLocaleLowerCase().includes(search.toLocaleLowerCase())) ||
        (Email != undefined &&
          Email.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
    );
  }, [legalClients, search]);

  const columns: GridColDef[] = [
    { field: 'idLegalClient', headerName: 'Nr.', width: 70 },
    { field: 'idArnoTopIntern', headerName: 'Namen.Nr.', width: 100 },
    {
      field: 'idContactGroup',
      headerName: 'Gruppe',
      width: 150,
      valueGetter: ({ row }: GridValueGetterParams) =>
        row.idContactGroup
          ? groups.find(g => g.idContactGroup === row.idContactGroup)
              ?.ContactGroup
          : '',
    },
    {
      field: 'last_login_at',
      headerName: 'Letzter Login',
      width: 150,
      renderCell: params =>
        params.value == undefined
          ? '-'
          : new Date(params.value).toLocaleString(),
    },
    {
      field: 'syncWithArnoTop',
      headerName: 'ArnoTop',
      width: 70,
      renderCell: params => (Boolean(params.value) ? <Check /> : <Close />),
    },
    {
      field: 'loginPossible',
      headerName: 'Login',
      width: 70,
      renderCell: params => (Boolean(params.value) ? <Check /> : <Close />),
    },
    {
      field: 'isReceivingEmailEnabled',
      headerName: 'E-Mails',
      width: 70,
      renderCell: params => (Boolean(params.value) ? <Check /> : <Close />),
    },
    { field: 'Email', headerName: 'E-Mail-Adresse', flex: 1 },
    { field: 'FirstName', headerName: 'Vorname', flex: 1 },
    { field: 'LastName', headerName: 'Nachname/Firma', flex: 1 },
  ];

  if (
    isLoadingLegalClients ||
    isLoadingMetadataLoginArray ||
    isLoadingDownloadedDocuments ||
    isLoadingDocumentRequestedArray ||
    isLoadingDocumentUploadedArray ||
    isLoadingLegalClientLogs
  ) {
    return <LoadingSpinner />;
  }

  if (
    !legalClients ||
    !metadataLoginArray ||
    !downloadedDocuments ||
    !documentRequestedArray ||
    !documentUploadedArray ||
    !legalClientLogs
  ) {
    return (
      <Box>
        <Typography variant='h5'>
          Es konnten keine Mandanten gefunden werden.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mr: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h4'>Mandanten</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ClientDasboardButton
              activeDashboard={activeDashboard}
              setActiveDashboard={setActiveDashboardWrapper}
            />
            <Button
              onClick={() => setShowArnoTopNoSyncDialog(true)}
              variant='outlined'
            >
              Neue über ARNOtop
            </Button>
            <Tooltip title='Neuer Mandant'>
              <IconButton onClick={() => navigate('new')}>
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ mt: 2 }}>
          <ClientDasboard
            legalClientArray={legalClients}
            metadataLoginArray={metadataLoginArray}
            documentDownloadedArray={downloadedDocuments}
            documentRequestedArray={documentRequestedArray}
            documentUploadedArray={documentUploadedArray}
            clientLogArray={legalClientLogs}
            activeDashboard={activeDashboard}
            setActiveDashboard={setActiveDashboard}
          />
        </Box>

        <TextField
          sx={{ my: 2 }}
          label='Suche'
          fullWidth
          value={search}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(event.target.value)
          }
        />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DataGrid
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              rows={filteredLegalClients}
              columns={columns}
              getRowId={row => row.idLegalClient}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 25,
                  },
                },
              }}
              autoHeight
              onRowClick={({ row }: { row: ILegalClientFullObject }) =>
                navigate(`${row.idLegalClient}`)
              }
            />
          </Grid>
        </Grid>
      </Box>

      {showArnoTopNoSyncDialog && (
        <AddFromArnotopDialog
          legalClientArray={filteredLegalClients}
          setLegalClientArray={() => {}} // TODO: Hinzufügen fehlt!
          onClose={() => setShowArnoTopNoSyncDialog(false)}
          onSave={refetchLegalClients}
        />
      )}
    </>
  );
};
